import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import { PRAIRI_SURFACES } from '../../constants/prairiSurfaces';
import { SUCCESS_FEE_LIVE } from '../../constants/successFee';
import companyIcon from '../../assets/icons/company.png';
import locationIcon from '../../assets/icons/location.png';
import expertiseIcon from '../../assets/icons/expertise.png';
import stageIcon from '../../assets/stage4.png';
import profileGrey from '../../assets/profile_icon__grey-correct_.png';

const PROFILE_PHOTO_MAX_BYTES = 5 * 1024 * 1024;
const PROFILE_PHOTO_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
export interface FavoriteConsentInvestorPreview {
  name?: string;
  country?: string;
  companyName?: string;
  photoUrl?: string;
  expertise?: string[];
  otherExpertise?: string;
  previousInvestments?: string;
  paysFee?: boolean;
}

interface FavoriteConsentPopupProps {
  isOpen: boolean;
  startupName: string;
  investorPreview?: FavoriteConsentInvestorPreview;
  onAllow: () => void | Promise<void>;
  onNotNow: () => void | Promise<void>;
}

function formatOffers(expertise?: string[], otherExpertise?: string): string {
  if (!expertise?.length) return 'Expertise in the field';
  return expertise
    .map(item => {
      if (item === 'industries-expertise') return 'Expertise in the field';
      if (item === 'industries-contacts') return 'Contacts in the field';
      if (item === 'other') return otherExpertise?.trim() || 'Other';
      return item;
    })
    .join(', ');
}

const ICON = '#c8c8c8';
const CARD_BORDER = '#444444';
const PANEL_BG = '#1E1E1C';

const HEART_PATH =
  'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z';

/** Filled white heart — for Sure! only. */
function FilledHeart({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="#ffffff" className={className} aria-hidden>
      <path d={HEART_PATH} />
    </svg>
  );
}

/** Same pictograms the startup sees on the investor profile. */
function ProfileIcon({ src }: { src: string }) {
  return <img src={src} alt="" aria-hidden className="h-7 w-7 flex-shrink-0 object-contain" />;
}

const FavoriteConsentPopup: React.FC<FavoriteConsentPopupProps> = ({
  isOpen,
  startupName,
  investorPreview,
  onAllow,
  onNotNow,
}) => {
  const { user } = useAuth();
  const { reloadProfile } = useProfile();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<'allow' | 'notNow' | null>(null);
  const [localPhotoUrl, setLocalPhotoUrl] = useState<string | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  useEffect(() => {
    if (!isOpen) {
      setPending(null);
      setPhotoError(null);
    }
  }, [isOpen]);

  const handleUploadPhotoClick = () => {
    if (photoUploading || pending) return;
    setPhotoError(null);
    photoInputRef.current?.click();
  };

  const handlePhotoSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) return;

    setPhotoError(null);
    if (!PROFILE_PHOTO_TYPES.includes(file.type)) {
      setPhotoError('Please upload a JPEG, PNG, or WebP image.');
      return;
    }
    if (file.size > PROFILE_PHOTO_MAX_BYTES) {
      setPhotoError('Image must be smaller than 5 MB.');
      return;
    }

    setPhotoUploading(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const upload = httpsCallable<
        { userId: string; file: { name: string; contentType: string; data: string } },
        { success: boolean; url?: string; error?: string }
      >(getFunctions(), 'uploadInvestorPicture');

      const result = await upload({
        userId: user.id,
        file: { name: file.name, contentType: file.type, data: base64 },
      });

      if (result.data.success && result.data.url) {
        const storedUrl = result.data.url;
        const freshUrl = storedUrl.includes('v=')
          ? storedUrl
          : `${storedUrl}${storedUrl.includes('?') ? '&' : '?'}v=${Date.now()}`;
        setLocalPhotoUrl(freshUrl);
        await reloadProfile();
      } else {
        setPhotoError(result.data.error || 'Upload failed. Please try again.');
      }
    } catch {
      setPhotoError('Upload failed. Please try again.');
    } finally {
      setPhotoUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !pending) onNotNow();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onNotNow, pending]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !pending) onNotNow();
  };

  const run = async (which: 'allow' | 'notNow') => {
    if (pending) return;
    setPending(which);
    try {
      if (which === 'allow') await onAllow();
      else await onNotNow();
    } finally {
      setPending(null);
    }
  };

  if (!isOpen) return null;

  const Spinner = () => (
    <span
      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
      aria-hidden
    />
  );

  const name = investorPreview?.name?.trim() || 'Your Name';
  const country = investorPreview?.country?.trim() || 'Location (Country)';
  const companyName = investorPreview?.companyName?.trim() || '';
  const previousInvestments = investorPreview?.previousInvestments?.trim() || '';
  const offers = formatOffers(investorPreview?.expertise, investorPreview?.otherExpertise);
  const photoUrl = localPhotoUrl || investorPreview?.photoUrl?.trim() || '';
  const showFriendlyFee = SUCCESS_FEE_LIVE && investorPreview?.paysFee !== false;

  return createPortal(
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/55 p-3 sm:p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="favorite-consent-title"
      aria-busy={pending != null}
    >
      <div
        className="max-h-[calc(100dvh-1.5rem)] w-full max-w-[40rem] overflow-y-auto rounded-[2rem] px-6 py-8 shadow-xl sm:px-9 sm:py-9"
        style={{
          backgroundColor: PANEL_BG,
          border: `1.5px solid ${PRAIRI_SURFACES.divider}`,
          fontFamily: 'Rubik, sans-serif',
        }}
        onClick={e => e.stopPropagation()}
      >
        <h2
          id="favorite-consent-title"
          className="mx-auto max-w-[34rem] text-center text-[22px] font-normal leading-snug sm:text-[26px]"
          style={{ color: ICON }}
        >
          Can we let <span className="font-bold text-white">{startupName}</span> know you&apos;re
          following their progress?
        </h2>

        <div className="mx-auto mt-8 w-full max-w-[22rem]">
          <p
            className="-ml-6 mb-4 text-left text-[15px] sm:-ml-8 sm:text-base"
            style={{ color: ICON }}
          >
            This is what they&apos;ll see....
          </p>
          <div
            className="rounded-2xl px-4 py-6 text-left sm:py-7"
            style={{
              backgroundColor: PRAIRI_SURFACES.profileCard,
              border: `1.5px solid ${CARD_BORDER}`,
            }}
          >
            <div className="mb-2 flex items-start gap-2.5">
              <div className="flex-shrink-0">
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  className="hidden"
                  onChange={handlePhotoSelected}
                />
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt=""
                    className="h-12 w-12 flex-shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <span
                    className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full"
                    style={{
                      backgroundColor: PRAIRI_SURFACES.profileCard,
                      border: `1.5px solid ${CARD_BORDER}`,
                    }}
                  >
                    <img
                      src={profileGrey}
                      alt=""
                      aria-hidden
                      className="h-[84%] w-[84%] translate-y-[14%] object-contain"
                    />
                  </span>
                )}
                {photoUrl ? null : (
                  <button
                    type="button"
                    onClick={handleUploadPhotoClick}
                    disabled={photoUploading || pending != null}
                    className="mt-1.5 block max-w-[5.5rem] text-left text-[11px] leading-tight underline underline-offset-2 disabled:opacity-50"
                    style={{ color: PRAIRI_SURFACES.trackerComplete }}
                  >
                    {photoUploading ? 'Uploading…' : 'upload photo'}
                  </button>
                )}
              </div>
              <p className="min-w-0 pt-2 pl-[1.125rem] text-base font-bold text-white">
                <span className="break-words">{name}</span>
                {showFriendlyFee ? (
                  <span
                    className="ml-2"
                    style={{ color: PRAIRI_SURFACES.trackerComplete }}
                    aria-hidden
                  >
                    ★
                  </span>
                ) : null}
              </p>
            </div>
            {photoError ? (
              <p className="mb-2 text-[11px] text-red-400" role="alert">
                {photoError}
              </p>
            ) : null}

            <div className="mt-4 ml-[1.5rem] border-l pl-3" style={{ borderColor: CARD_BORDER }}>
              <div className="mb-4 flex items-start gap-3">
                <ProfileIcon src={companyIcon} />
                <div className="min-w-0">
                  {companyName ? <p className="text-sm text-white">{companyName}</p> : null}
                  <p className="mt-1.5 text-xs underline" style={{ color: ICON }}>
                    Contact this investor
                  </p>
                </div>
              </div>

              <div className="mb-2 flex items-start gap-3">
                <ProfileIcon src={locationIcon} />
                <p className="text-sm text-white">{country}</p>
              </div>

              <div className="mb-2 flex items-start gap-3">
                <ProfileIcon src={expertiseIcon} />
                <div>
                  <p className="text-sm font-bold text-white">Investor offers:</p>
                  <p className="text-sm" style={{ color: ICON }}>
                    {offers}
                  </p>
                </div>
              </div>

              {previousInvestments ? (
                <div className="mb-1 flex items-start gap-3">
                  <ProfileIcon src={stageIcon} />
                  <div>
                    <p className="text-sm font-bold text-white">Previous investments include:</p>
                    <p className="text-sm" style={{ color: ICON }}>
                      {previousInvestments}
                    </p>
                  </div>
                </div>
              ) : null}
            </div>

            {SUCCESS_FEE_LIVE ? (
              showFriendlyFee ? (
                <div
                  className="rounded-xl px-3 py-3"
                  style={{
                    backgroundColor: PRAIRI_SURFACES.profileCard,
                    border: `1.5px solid ${CARD_BORDER}`,
                  }}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className="mt-0.5"
                      style={{ color: PRAIRI_SURFACES.trackerComplete }}
                      aria-hidden
                    >
                      ★
                    </span>
                    <div>
                      <p className="text-sm font-bold text-white">Startup-Friendly Investor</p>
                      <p className="mt-0.5 text-xs leading-snug" style={{ color: ICON }}>
                        This investor has generously agreed to pay PRAIRI&apos;s &apos;success
                        fee&apos; from their own funds.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="rounded-xl px-3 py-3"
                  style={{
                    backgroundColor: PRAIRI_SURFACES.profileCard,
                    border: `1.5px solid ${CARD_BORDER}`,
                  }}
                >
                  <p className="text-xs leading-snug" style={{ color: ICON }}>
                    PRAIRI&apos;s &apos;success fee&apos; (2% of this investor&apos;s total
                    investment) will be allocated from the amount they provide you.
                  </p>
                </div>
              )
            ) : null}
          </div>
        </div>

        <div className="relative mt-5 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          <button
            type="button"
            onClick={() => run('notNow')}
            disabled={pending != null}
            className="ml-4 justify-self-start text-sm underline underline-offset-4 transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-60 sm:ml-6"
            style={{
              color: PRAIRI_SURFACES.trackerPending,
              textDecorationColor: PRAIRI_SURFACES.trackerPending,
            }}
          >
            {pending === 'notNow' ? <Spinner /> : null}
            Not this time
          </button>
          <button
            type="button"
            onClick={() => run('allow')}
            disabled={pending != null}
            className="inline-flex h-11 min-w-[13.25rem] items-center justify-center gap-2 rounded-full px-10 text-base font-medium text-white transition-colors hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-60"
            style={{
              backgroundColor: PANEL_BG,
              border: `1px solid ${PRAIRI_SURFACES.trackerComplete}`,
            }}
          >
            {pending === 'allow' ? <Spinner /> : <FilledHeart className="h-4 w-4 flex-shrink-0" />}
            Sure!
          </button>
          <span aria-hidden="true" />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FavoriteConsentPopup;
